import * as types from '../mutation-types'
import { api } from '@/modules/api/index'
import tokenService from '../../../plugins/tokenService'
import decodeJWT from '@/utils/decodeJWT'
import { pathOr, isEmpty } from 'ramda'

const state = {
  list: [],
  totalPages: '',
  totalUserItems: '',
  scopes: [],
  userDetails: {},
  userActionLogs: [],
  userActionTypes: [],
  actionsTotalPages: '',
  actionsTotalItems: ''
}

const getters = {
  getUsers: state => state,
  getUsersList: state => state.list,
  getUsersListWithCurrentUser: state => isEmpty(state.userDetails) ? state.list : [...state.list, state.userDetails],
  getScopesList: state => state.scopes,
  getTotalUserItems: state => state.totalUserItems,
  getCurrentUserDetails: state => state.userDetails,
  getUsersActions: state => state.userActionLogs,
  getUserActionTypes: state => state.userActionTypes,
  getActionsTotalPages: state => state.actionsTotalPages,
  getActionsTotalItems: state => state.actionsTotalItems
}

const actions = {
  async fetchScopesList ({ commit }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.scopes())
      commit(types.SET_SCOPES_LIST, data)
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on fetch users')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async fetchUsers ({ commit }, {page = 0, itemsOnPage = 99999, userName, email} = {}) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.getUsersList(userName, email), {params: {page, itemsOnPage}})
      commit(types.SET_USERS_LIST, data)
      commit(types.SET_SPINNER_STATUS, false)
      return data
    } catch (err) {
      commit(types.SET_SPINNER_STATUS, false)
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on fetch users')
      throw err
    }
  },
  async updateUser ({ dispatch, commit }, {id, scopes}) {
    try {
      const { data } = await this.$axios.patch(api.users.req(), { id, scopes })
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on update user')
      throw err
    }
  },
  async deleteUser ({ dispatch, commit }, id) {
    try {
      const { data } = await this.$axios({ method: 'delete', url: api.users.req(id) })
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on delete user')
      throw err
    }
  },
  async createUser ({ dispatch, commit }, {email, userName, scopes}) {
    try {
      const response = await this.$axios.post(api.users.post(), {email, userName, scopes})
      return response
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on create user')
      throw err
    }
  },
  async fetchUsersForSuperAdmin ({ commit }, {page = 0, itemsOnPage = 99999, userName, email} = {}) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.getUsersListForSuperAdmin(userName, email), {params: {page, itemsOnPage}})
      commit(types.SET_USERS_LIST, data)
      commit(types.SET_SPINNER_STATUS, false)
      return data
    } catch (err) {
      commit(types.SET_SPINNER_STATUS, false)
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on fetch users')
      throw err
    }
  },
  async updateUserBySuperAdmin ({ dispatch, commit }, {id, scopes}) {
    try {
      const { data } = await this.$axios.patch(api.users.reqBySuperAdmin(), { id, scopes })
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on update user')
      throw err
    }
  },
  async deleteUserBySuperAdmin ({ dispatch, commit }, id) {
    try {
      const { data } = await this.$axios({ method: 'delete', url: api.users.reqBySuperAdmin(id) })
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed on delete user')
      throw err
    }
  },
  async logoutSingleAdmin ({ dispatch, commit }, idsList) {
    try {
      const { data } = await this.$axios.post(api.users.logoutAdmin(), idsList)
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to logout user')
      throw err
    }
  },
  async logoutAllAdmins ({ dispatch, commit }) {
    try {
      const { data } = await this.$axios({ method: 'post', url: api.users.logoutAdmins() })
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to logout user')
      throw err
    }
  },
  async fetchCurrentUserDetails ({ commit, dispatch }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.getDetails())
      commit(types.SET_USER_DETAILS, data)
      await dispatch('setIdOfCurrentUser')
      commit(types.SET_SPINNER_STATUS, false)
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to fetch user details')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  setIdOfCurrentUser ({ commit }) {
    const id = pathOr(null, ['payload', 'userId'], decodeJWT(tokenService.getAccessToken()))
    if (!id) return
    commit(types.SET_ID_OF_CURRENT_USER, id)
  },
  async fetchAdminsActions ({ commit }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.getUsersActions(payload))
      commit(types.SET_USERS_ACTIONS, data)
      commit(types.SET_SPINNER_STATUS, false)
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to fetch user details')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async fetchActionsTypes ({ commit }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.users.getUsersActionsTypes())
      commit(types.SET_USERS_ACTIONS_TYPES, data)
      commit(types.SET_SPINNER_STATUS, false)
      return data
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to fetch user actions types')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  }
}

const mutations = {
  [types.SET_USERS_LIST] (state, usersData) {
    state.list = usersData.users
    state.totalPages = usersData.totalPages
    state.totalUserItems = usersData.totalItems
  },
  [types.SET_USERS_ACTIONS] (state, {userActionLogs, totalPages, totalItems}) {
    state.userActionLogs = userActionLogs
    state.actionsTotalPages = totalPages
    state.actionsTotalItems = totalItems
  },
  [types.SET_USERS_ACTIONS_TYPES] (state, types) {
    state.userActionTypes = types
  },
  [types.SET_SCOPES_LIST] (state, scopes) {
    state.scopes = [...scopes]
  },
  [types.SET_USER_DETAILS] (state, data) {
    state.userDetails = data
  },
  [types.SET_ID_OF_CURRENT_USER] (state, id) {
    state.userDetails = { ...state.userDetails, id }
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/users.js